import React, { useState, useEffect } from 'react';
import { Modal, Form, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faSearch,
  faExclamationTriangle,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import {
  getRealCustomersForTesting,
  RealCustomer
} from 'services/hvaagent.service';
import HVASpinner from 'components/base/HVASpinner';
import Nodata from 'assets/img/spot-illustrations/404-illustration.png';

interface CustomerSelectorModalProps {
  show: boolean;
  onHide: () => void;
  onSelectCustomer: (customer: RealCustomer) => void;
}

const HVACustomerSelectorModal: React.FC<CustomerSelectorModalProps> = ({
  show,
  onHide,
  onSelectCustomer
}) => {
  const [customers, setCustomers] = useState<RealCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  // Cargar clientes cuando se abre el modal o cambia la página/búsqueda
  useEffect(() => {
    if (show) {
      loadCustomers();
    }
  }, [show, currentPage, searchTerm]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getRealCustomersForTesting({
        page: currentPage,
        pageSize,
        search: searchTerm
      });

      setCustomers(response.customers);
      setTotalRecords(response.total);
      setTotalPages(Math.ceil(response.total / pageSize));
    } catch (err) {
      console.error('Error loading customers:', err);
      setError(err instanceof Error ? err.message : 'Error loading customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleSelectCustomer = (customer: RealCustomer) => {
    onSelectCustomer(customer);
    handleClose();
  };

  const handleClose = () => {
    // Reset state when closing
    setSearchTerm('');
    setCurrentPage(1);
    setError(null);
    onHide();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Formatear fecha para mostrar
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faUserCircle} className="me-2" />
          Select Real Customer for Testing
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-6">
        {/* Search Bar */}
        <Form onSubmit={handleSearch} className="mb-4">
          <div className="d-flex gap-2">
            <Form.Control
              type="text"
              placeholder="Search by name, phone, or TRN..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="bg-body"
            />
            <Button type="submit" variant="primary" disabled={loading}>
              <FontAwesomeIcon icon={faSearch} className="me-2" />
              Search
            </Button>
          </div>
        </Form>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <HVASpinner />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-body rounded p-4 text-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="fs-3 text-danger mb-2"
            />
            <p className="mb-0 text-danger fs-9">{error}</p>
            <Button
              variant="link"
              size="sm"
              onClick={loadCustomers}
              className="mt-2"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && customers.length === 0 && (
          <div className="text-center py-5">
            <img src={Nodata} alt="No Data" width={250} />
            <p className="text-muted mb-0 fs-9">
              {searchTerm
                ? 'No customers found matching your search'
                : 'No customers available'}
            </p>
          </div>
        )}

        {/* Table with Results */}
        {!loading && !error && customers.length > 0 && (
          <>
            <div className="mb-3 text-muted fs-9">
              Showing {(currentPage - 1) * pageSize + 1} to{' '}
              {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}{' '}
              customers
            </div>

            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-body fs-9">
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>TRN</th>
                    <th>Date of Birth</th>
                    <th>Reference</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="fs-9">
                  {customers.map(customer => (
                    <tr
                      key={customer.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSelectCustomer(customer)}
                      className="pt-0 pb-0"
                    >
                      <td className="pt-1 pb-1 px-2">
                        {customer.customername}
                      </td>
                      <td className="pt-1 pb-1">
                        {customer.customerphone || 'N/A'}
                      </td>
                      <td className="pt-1 pb-1">
                        {customer.customertrn || 'N/A'}
                      </td>
                      <td className="pt-1 pb-1">
                        {formatDate(customer.customerdob)}
                      </td>
                      <td className="pt-1 pb-1">
                        {customer.customerreference || 'N/A'}
                      </td>
                      <td className="text-center pt-1 pb-1">
                        <Button
                          variant="subtle-secondary"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            handleSelectCustomer(customer);
                          }}
                          className="pt-1 pb-0"
                        >
                          <span className="text-secondary">
                            {' '}
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="me-1"
                            />
                            Select
                          </span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <div className="text-muted fs-9">
                  Page {currentPage} of {totalPages}
                </div>

                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HVACustomerSelectorModal;
